import { createContext, useContext, useEffect, useState, useCallback } from "react";

const PinCtx = createContext(null);

const STORAGE_KEY = "wmf_pin_hash";
const ENABLED_KEY = "wmf_pin_enabled";
const BIOM_ENABLED_KEY = "wmf_biom_enabled";
const BIOM_CRED_ID_KEY = "wmf_biom_cred_id";
const AUTO_LOCK_MIN = 5;
const LAST_ACTIVE_KEY = "wmf_pin_last_active";
const ATTEMPTS_KEY = "wmf_pin_attempts";
const LOCKOUT_UNTIL_KEY = "wmf_pin_lockout_until";

function _hasWebAuthn() {
    return (
        typeof window !== "undefined" &&
        typeof window.PublicKeyCredential !== "undefined" &&
        typeof navigator.credentials?.create === "function"
    );
}

async function isBiometricAvailable() {
    if (!_hasWebAuthn()) return false;
    try {
        return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
        return false;
    }
}

async function enrollBiometric() {
    if (!_hasWebAuthn()) throw new Error("Biometric not supported on this device");
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const userId = crypto.getRandomValues(new Uint8Array(16));
    const cred = await navigator.credentials.create({
        publicKey: {
            challenge,
            rp: { name: "Lokdhara Microfinance" },
            user: {
                id: userId,
                name: "lokdhara-user",
                displayName: "Lokdhara User",
            },
            pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
            authenticatorSelection: {
                authenticatorAttachment: "platform",
                userVerification: "required",
            },
            timeout: 60000,
            attestation: "none",
        },
    });
    if (!cred) throw new Error("Enrolment cancelled");
    const credId = btoa(String.fromCharCode(...new Uint8Array(cred.rawId)));
    localStorage.setItem(BIOM_CRED_ID_KEY, credId);
    localStorage.setItem(BIOM_ENABLED_KEY, "1");
    return true;
}

async function verifyBiometric() {
    if (!_hasWebAuthn()) return false;
    const credId = localStorage.getItem(BIOM_CRED_ID_KEY);
    if (!credId) return false;
    try {
        const challenge = crypto.getRandomValues(new Uint8Array(32));
        const idBytes = Uint8Array.from(atob(credId), (c) => c.charCodeAt(0));
        const assertion = await navigator.credentials.get({
            publicKey: {
                challenge,
                allowCredentials: [{ id: idBytes, type: "public-key" }],
                userVerification: "required",
                timeout: 60000,
            },
        });
        return !!assertion;
    } catch {
        return false;
    }
}

// Brute force tiers
function lockoutSeconds(attempts) {
    if (attempts < 5) return 0;
    if (attempts < 10) return 60;   // 1 minute
    if (attempts < 15) return 5 * 60; // 5 minutes
    return 30 * 60;                  // 30 minutes
}

async function sha256(text) {
    const enc = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

export function PinProvider({ children }) {
    const [enabled, setEnabled] = useState(() => localStorage.getItem(ENABLED_KEY) === "1");
    const [biomEnabled, setBiomEnabled] = useState(() => localStorage.getItem(BIOM_ENABLED_KEY) === "1");
    const [biomAvailable, setBiomAvailable] = useState(false);
    const [locked, setLocked] = useState(false);
    const [lockoutUntil, setLockoutUntil] = useState(() =>
        parseInt(localStorage.getItem(LOCKOUT_UNTIL_KEY) || "0", 10)
    );
    const [attempts, setAttempts] = useState(() =>
        parseInt(localStorage.getItem(ATTEMPTS_KEY) || "0", 10)
    );

    useEffect(() => {
        isBiometricAvailable().then(setBiomAvailable);
    }, []);

    useEffect(() => {
        if (!enabled) {
            setLocked(false);
            return;
        }
        const last = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || "0", 10);
        const now = Date.now();
        if (!last || now - last > AUTO_LOCK_MIN * 60 * 1000) {
            setLocked(true);
        }
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;
        const beat = () => localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
        beat();
        const events = ["mousedown", "keydown", "touchstart"];
        events.forEach((ev) => window.addEventListener(ev, beat));
        const id = setInterval(() => {
            const last = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || "0", 10);
            if (last && Date.now() - last > AUTO_LOCK_MIN * 60 * 1000) {
                setLocked(true);
            }
        }, 30 * 1000);
        return () => {
            events.forEach((ev) => window.removeEventListener(ev, beat));
            clearInterval(id);
        };
    }, [enabled]);

    const setPin = useCallback(async (pin) => {
        const h = await sha256(pin);
        localStorage.setItem(STORAGE_KEY, h);
        localStorage.setItem(ENABLED_KEY, "1");
        localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
        localStorage.removeItem(ATTEMPTS_KEY);
        localStorage.removeItem(LOCKOUT_UNTIL_KEY);
        setAttempts(0);
        setLockoutUntil(0);
        setEnabled(true);
        setLocked(false);
    }, []);

    const disablePin = useCallback(async (pin) => {
        const stored = localStorage.getItem(STORAGE_KEY);
        const h = await sha256(pin);
        if (stored !== h) return false;
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(ENABLED_KEY);
        localStorage.removeItem(ATTEMPTS_KEY);
        localStorage.removeItem(LOCKOUT_UNTIL_KEY);
        setAttempts(0);
        setLockoutUntil(0);
        setEnabled(false);
        setLocked(false);
        return true;
    }, []);

    const unlock = useCallback(async (pin) => {
        const now = Date.now();
        const until = parseInt(localStorage.getItem(LOCKOUT_UNTIL_KEY) || "0", 10);
        if (until && now < until) {
            setLockoutUntil(until);
            return { ok: false, lockoutUntil: until };
        }
        const stored = localStorage.getItem(STORAGE_KEY);
        const h = await sha256(pin);
        if (stored === h) {
            localStorage.removeItem(ATTEMPTS_KEY);
            localStorage.removeItem(LOCKOUT_UNTIL_KEY);
            setAttempts(0);
            setLockoutUntil(0);
            setLocked(false);
            localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
            return { ok: true };
        }
        // Wrong PIN
        const newAttempts = (parseInt(localStorage.getItem(ATTEMPTS_KEY) || "0", 10) || 0) + 1;
        localStorage.setItem(ATTEMPTS_KEY, String(newAttempts));
        setAttempts(newAttempts);
        const wait = lockoutSeconds(newAttempts);
        let newUntil = 0;
        if (wait > 0) {
            newUntil = Date.now() + wait * 1000;
            localStorage.setItem(LOCKOUT_UNTIL_KEY, String(newUntil));
            setLockoutUntil(newUntil);
        }
        return { ok: false, attempts: newAttempts, lockoutUntil: newUntil };
    }, []);

    const lockNow = useCallback(() => {
        if (enabled) setLocked(true);
    }, [enabled]);

    const resetPin = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(ENABLED_KEY);
        localStorage.removeItem(ATTEMPTS_KEY);
        localStorage.removeItem(LOCKOUT_UNTIL_KEY);
        setEnabled(false);
        setLocked(false);
        setAttempts(0);
        setLockoutUntil(0);
    }, []);

    const enableBiometric = useCallback(async () => {
        const ok = await enrollBiometric();
        if (ok) setBiomEnabled(true);
        return ok;
    }, []);

    const disableBiometric = useCallback(() => {
        localStorage.removeItem(BIOM_ENABLED_KEY);
        localStorage.removeItem(BIOM_CRED_ID_KEY);
        setBiomEnabled(false);
    }, []);

    const unlockWithBiometric = useCallback(async () => {
        const ok = await verifyBiometric();
        if (ok) {
            localStorage.removeItem(ATTEMPTS_KEY);
            localStorage.removeItem(LOCKOUT_UNTIL_KEY);
            setAttempts(0);
            setLockoutUntil(0);
            setLocked(false);
            localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
        }
        return ok;
    }, []);

    return (
        <PinCtx.Provider
            value={{
                enabled, locked, attempts, lockoutUntil,
                biomEnabled, biomAvailable,
                setPin, disablePin, unlock, lockNow, resetPin,
                enableBiometric, disableBiometric, unlockWithBiometric,
            }}
        >
            {children}
        </PinCtx.Provider>
    );
}

export function usePin() {
    return useContext(PinCtx);
}
